import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import { CheckCircle2, AlertCircle, Cloud, Shield, Zap, Server, CreditCard, Loader2, Globe, Wifi, User, FileText, Mail, Clock, XCircle, SkipForward } from 'lucide-react';
import { RevealWrapper } from '../hooks/useReveal';
import { toast } from 'sonner';
import { useCookieConsent, COOKIE_CATEGORIES } from '../context/CookieContext';
import { useCart } from '../context/CartContext';
import { 
  addEcommerceItem, 
  trackEcommerceOrder, 
  trackEvent,
  trackGoal
} from '../services/matomo';

export const ProvisioningPage = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentPending, setPaymentPending] = useState(false);
  const { hasConsent } = useCookieConsent();
  const { clearCart } = useCart();
  
  // Track if provisioning has been started (to prevent duplicate toasts)
  const provisioningStartedRef = useRef(false);
  // Track if payment has been verified (to prevent duplicate verification)
  const paymentVerifiedRef = useRef(false);
  // Track if order has been tracked in analytics
  const orderTrackedRef = useRef(false);
  // Track if cart has been cleared (only on success, NOT on payment initiation)
  const cartClearedRef = useRef(false);
  // Track polling interval
  const pollingRef = useRef(null);

  // Check for payment return parameters
  const sessionId = searchParams.get('session_id');
  const paypalSuccess = searchParams.get('paypal');
  const paypalOrderId = searchParams.get('paypal_order_id');

  // Icon mapping for provisioning steps
  const stepIcons = {
    contact_create: User,
    domain_registration: Globe,
    domain_job_wait: Clock,
    dns_setup: Wifi,
    dns_zone: Wifi,
    dns_verification: Shield,
    nextcloud_create: Cloud,
    nextcloud_ready: CheckCircle2,
    invoice_create: FileText,
    email_welcome: Mail
  };

  useEffect(() => {
    if (!orderId) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

    // Handle Stripe payment verification
    const verifyStripePayment = async () => {
      if (!sessionId) return true;
      
      setPaymentPending(true);
      
      // Poll for payment status
      const maxAttempts = 10;
      let attempts = 0;
      
      while (attempts < maxAttempts) {
        try {
          const response = await fetch(`${backendUrl}/api/payments/status/${sessionId}`);
          if (!response.ok) throw new Error('Status check failed');
          
          const data = await response.json();
          
          if (data.payment_status === 'paid') {
            toast.success('Zahlung erfolgreich! 🎉');
            setPaymentPending(false);
            
            // Track successful order in analytics (only once)
            if (!orderTrackedRef.current && hasConsent(COOKIE_CATEGORIES.ANALYTICS)) {
              orderTrackedRef.current = true;
              try {
                trackEcommerceOrder({
                  orderId: orderId,
                  revenue: data.order_total || 0,
                  items: data.order_items || []
                });
                trackGoal('order_completed', data.order_total || 0);
              } catch (error) {
                console.error('Analytics tracking error:', error);
              }
            }
            
            return true;
          } else if (data.payment_status === 'expired' || data.payment_status === 'failed') {
            toast.error('Zahlung fehlgeschlagen oder abgelaufen.');
            setPaymentPending(false);
            navigate('/checkout?payment_failed=true');
            return false;
          }
          
          await new Promise(resolve => setTimeout(resolve, 2000));
          attempts++;
        } catch (error) {
          console.error('Payment status error:', error);
          attempts++;
        }
      }
      
      setPaymentPending(false);
      return true;
    };

    // Handle PayPal payment verification
    const verifyPayPalPayment = async () => {
      if (paypalSuccess !== 'success' && paypalSuccess !== 'mock') return true;
      
      setPaymentPending(true);
      
      try {
        // If we have paypalOrderId from URL, use it directly
        let paypalId = paypalOrderId;
        
        // If not in URL, try to get it from the order status
        if (!paypalId) {
          const statusResponse = await fetch(`${backendUrl}/api/orders/${orderId}/status`);
          if (statusResponse.ok) {
            const statusData = await statusResponse.json();
            paypalId = statusData.paypal_order_id;
          }
        }
        
        if (paypalSuccess === 'mock' && paypalId) {
          const response = await fetch(`${backendUrl}/api/payments/status/${paypalId}`);
          if (response.ok) {
            const data = await response.json();
            if (data.payment_status === 'paid') {
              toast.success('PayPal-Zahlung erfolgreich! 🎉');
              setPaymentPending(false);
              
              if (!orderTrackedRef.current && hasConsent(COOKIE_CATEGORIES.ANALYTICS)) {
                orderTrackedRef.current = true;
                try {
                  trackEcommerceOrder({
                    orderId: orderId,
                    revenue: data.order_total || 0,
                    items: data.order_items || []
                  });
                  trackGoal('order_completed', data.order_total || 0);
                } catch (error) {
                  console.error('Analytics tracking error:', error);
                }
              }
              
              return true;
            }
          }
        }
        
        if (paypalId) {
          console.log(`[PAYPAL] Capturing order: ${paypalId}`);
          const captureResponse = await fetch(
            `${backendUrl}/api/payments/paypal/capture/${paypalId}`,
            { method: 'POST' }
          );
          
          if (captureResponse.ok) {
            toast.success('PayPal-Zahlung erfolgreich! 🎉');
            setPaymentPending(false);
            
            if (!orderTrackedRef.current && hasConsent(COOKIE_CATEGORIES.ANALYTICS)) {
              orderTrackedRef.current = true;
              try {
                const captureData = await captureResponse.json();
                trackEcommerceOrder({
                  orderId: orderId,
                  revenue: captureData.order_total || 0,
                  items: captureData.order_items || []
                });
                trackGoal('order_completed', captureData.order_total || 0);
              } catch (error) {
                console.error('Analytics tracking error:', error);
              }
            }
            
            return true;
          } else {
            const errorData = await captureResponse.json().catch(() => ({}));
            console.error('[PAYPAL] Capture failed:', errorData);
          }
        } else {
          console.error('[PAYPAL] No paypal_order_id found for capture');
        }
      } catch (error) {
        console.error('PayPal verification error:', error);
      }
      
      setPaymentPending(false);
      return true;
    };

    const fetchStatus = async () => {
      try {
        // Verify payment ONLY ONCE (not on every poll)
        if (!paymentVerifiedRef.current) {
          if (sessionId) {
            paymentVerifiedRef.current = true;
            const paymentOk = await verifyStripePayment();
            if (!paymentOk) return;
          } else if (paypalSuccess) {
            paymentVerifiedRef.current = true;
            const paymentOk = await verifyPayPalPayment();
            if (!paymentOk) return;
          } else {
            paymentVerifiedRef.current = true;
          }
        }
        
        // FIX (BUG-11): Warenkorb erst NACH erfolgreicher Zahlung leeren
        // (nicht bei Zahlungsinitiierung, damit ein Abbruch die Config nicht löscht)
        if (!cartClearedRef.current && paymentVerifiedRef.current) {
          cartClearedRef.current = true;
          try {
            clearCart();
          } catch (e) {
            console.error('Error clearing cart:', e);
          }
        }
        
        // Only try to start provisioning ONCE (not on every poll)
        if (!provisioningStartedRef.current) {
          provisioningStartedRef.current = true;
          try {
            const startResponse = await fetch(`${backendUrl}/api/provisioning/${orderId}/start`, {
              method: 'POST'
            });
            const startData = await startResponse.json();
            
            if (startData.is_free) {
              toast.success('Kostenlose Bestellung! Installation startet... 🎉');
            }
          } catch (error) {
            console.error('Error starting provisioning:', error);
          }
        }
        
        const response = await fetch(`${backendUrl}/api/provisioning/${orderId}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch provisioning status');
        }

        const data = await response.json();
        console.log('[Provisioning] Status update:', data.status, 'Progress:', data.progress, 'Steps:', data.provisioning_steps?.length || 0);
        setStatus(data);
        setLoading(false);

        // Stop polling if ready or failed
        if (data.status === 'ready' || data.status === 'failed') {
          if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        }
      } catch (error) {
        console.error('Error fetching status:', error);
        setLoading(false);
      }
    };

    // Initial fetch
    fetchStatus();
    
    // Set up polling interval (every 2 seconds for live updates)
    pollingRef.current = setInterval(fetchStatus, 2000);
    
    // Cleanup on unmount
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [orderId, sessionId, paypalSuccess, paypalOrderId, navigate, hasConsent]);

  // Render step icon based on status
  const renderStepIcon = (step) => {
    const IconComponent = stepIcons[step.key] || Server;
    
    if (step.status === 'success') {
      return <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />;
    }
    if (step.status === 'failed') {
      return <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />;
    }
    if (step.status === 'skipped') {
      return <SkipForward className="h-5 w-5 text-amber-500 flex-shrink-0" />;
    }
    if (step.status === 'running') {
      return (
        <div className="relative flex-shrink-0">
          <Loader2 className="h-5 w-5 text-primary animate-spin" />
        </div>
      );
    }
    // pending
    return <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30 flex-shrink-0" />;
  };

  // Calculate progress from steps
  const calculateProgress = () => {
    if (!status?.provisioning_steps || status.provisioning_steps.length === 0) {
      return status?.progress || 0;
    }
    
    const steps = status.provisioning_steps;
    const completedSteps = steps.filter(s => s.status === 'success' || s.status === 'skipped').length;
    const runningSteps = steps.filter(s => s.status === 'running').length;
    
    // Each completed step is worth 100/total%, running step adds half
    const progress = ((completedSteps + runningSteps * 0.5) / steps.length) * 100;
    return Math.min(Math.round(progress), 99); // Never show 100% until truly complete
  };

  // Show payment pending state
  if (paymentPending) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <CreditCard className="mx-auto h-12 w-12 animate-pulse text-primary" />
          <p className="mt-4 text-lg font-medium">Zahlung wird verarbeitet...</p>
          <p className="mt-2 text-muted-foreground">Bitte warte einen Moment.</p>
          <Loader2 className="mx-auto mt-4 h-6 w-6 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <Cloud className="mx-auto h-12 w-12 animate-pulse text-primary" />
          <p className="mt-4 text-muted-foreground">Lade Status...</p>
          <Loader2 className="mx-auto mt-2 h-5 w-5 animate-spin text-primary/50" />
        </div>
      </div>
    );
  }

  const progress = calculateProgress();
  const steps = status?.provisioning_steps || [];
  const currentStep = steps.find(s => s.status === 'running');
  const hasFailedSteps = steps.some(s => s.status === 'failed');

  return (
    <div className="py-16 md:py-24" data-testid="provisioning-page">
      <div className="container mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <RevealWrapper>
          <Card className="glass-card" data-testid="provisioning-card">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
                {status?.status === 'ready' ? (
                  <CheckCircle2 className="h-10 w-10 text-emerald-500" />
                ) : status?.status === 'failed' || hasFailedSteps ? (
                  <AlertCircle className="h-10 w-10 text-amber-500" />
                ) : (
                  <div className="relative">
                    <Cloud className="h-10 w-10 text-primary" />
                  </div>
                )}
              </div>
              <CardTitle className="font-display text-3xl">
                {status?.status === 'ready'
                  ? '✅ Deine Helden-Wolke ist bereit!'
                  : status?.status === 'failed' || hasFailedSteps
                  ? '⚠️ Ein Schritt benötigt Aufmerksamkeit'
                  : '🦸 Deine Helden-Wolke wird gebaut'}
              </CardTitle>
              <CardDescription className="text-base">
                {currentStep 
                  ? `${currentStep.name}...` 
                  : status?.message || 'Einen Moment bitte...'}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              {status && status.status !== 'ready' && (
                <>
                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <Progress
                      value={progress}
                      className="h-3"
                      data-testid="provisioning-progress-bar"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{progress}% abgeschlossen</span>
                      {currentStep && (
                        <span className="flex items-center gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          {currentStep.name}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Live Steps */}
                  {steps.length > 0 && (
                    <div className="space-y-2 rounded-lg bg-muted/30 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium">Fortschritt:</p>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <span>Live-Updates</span>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        {steps.sort((a, b) => a.order - b.order).map((step) => (
                          <div
                            key={step.key}
                            className={`flex items-center gap-3 p-2 rounded-md transition-all ${
                              step.status === 'running' 
                                ? 'bg-primary/10 border border-primary/20' 
                                : step.status === 'failed'
                                ? 'bg-red-500/10 border border-red-500/20'
                                : step.status === 'success'
                                ? 'bg-emerald-500/5'
                                : ''
                            }`}
                          >
                            {renderStepIcon(step)}
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm truncate ${
                                step.status === 'running' ? 'font-medium text-primary' :
                                step.status === 'success' ? 'text-emerald-600' :
                                step.status === 'failed' ? 'text-red-600' :
                                step.status === 'skipped' ? 'text-amber-600' :
                                'text-muted-foreground'
                              }`}>
                                {step.name}
                              </p>
                              {step.status === 'running' && (
                                <p className="text-xs text-muted-foreground truncate">
                                  {step.description}
                                </p>
                              )}
                              {step.status === 'failed' && step.error && (
                                <p className="text-xs text-red-500 truncate">
                                  {step.error}
                                </p>
                              )}
                            </div>
                            {step.status === 'running' && (
                              <span className="text-xs text-primary hidden sm:inline">Läuft</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Fallback message when no steps */}
                  {steps.length === 0 && (
                    <div className="text-center py-4 text-muted-foreground">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                      <p className="text-sm">Provisioning wird initialisiert...</p>
                    </div>
                  )}
                </>
              )}

              {status?.status === 'ready' && status?.instance?.url && (
                <div className="space-y-4">
                  <div className="rounded-lg bg-green-500/10 border border-green-500/20 p-4">
                    <p className="text-sm font-medium text-green-600">🚀 Deine Helden-Wolke URL:</p>
                    <a
                      href={status.instance.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 block break-all text-lg font-mono text-primary hover:underline"
                      data-testid="instance-url"
                    >
                      {status.instance.url}
                    </a>
                  </div>

                  <div className="flex gap-4">
                    <Button
                      asChild
                      className="flex-1 bg-gradient-to-r from-primary to-blue-600"
                      data-testid="button-open-cloud"
                    >
                      <a href={status.instance.url} target="_blank" rel="noopener noreferrer">
                        🚀 Zur Wolke fliegen
                      </a>
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/portal')}
                      data-testid="button-to-portal"
                    >
                      🦸 Zum Helden-Portal
                    </Button>
                  </div>
                </div>
              )}

              {(status?.status === 'failed' || hasFailedSteps) && status?.status !== 'ready' && (
                <div className="space-y-4">
                  <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-4">
                    <p className="text-sm text-foreground font-medium mb-2">
                      ⚠️ Bei der Einrichtung deiner Helden-Wolke ist ein Problem aufgetreten.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Keine Sorge – unser Helden-Team wurde bereits automatisch informiert und kümmert sich darum! 
                      Du musst nichts weiter tun. Sobald alles bereit ist, erhältst du eine E-Mail mit deinen Zugangsdaten.
                    </p>
                    <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-amber-500/20">
                      Falls du Fragen hast, kannst du uns jederzeit über den Support kontaktieren.
                    </p>
                  </div>

                  <div className="flex gap-4">
                    <Button variant="outline" onClick={() => navigate('/portal/support')}>
                      🦸‍♂️ Helden-Support kontaktieren
                    </Button>
                    <Button variant="ghost" onClick={() => navigate('/portal')}>
                      Zum Portal
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </RevealWrapper>
      </div>
    </div>
  );
};

export default ProvisioningPage;
